import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import MuiLink from '@mui/material/Link'
import { Link as RouterLink } from 'react-router'

export type CancellationDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: (refundType: 'credit' | 'monetary') => void
  joinRequestId: string
  transactionAmount: number
  stripeFee: number
  isBuddyDive: boolean
  isProcessing: boolean
}

const formatCurrency = (amount: number): string => `$${amount.toFixed(2)}`

const CancellationDialog = ({
  open,
  onClose,
  onConfirm,
  transactionAmount,
  stripeFee,
  isBuddyDive,
  isProcessing
}: CancellationDialogProps) => {
  const monetaryRefundAmount = transactionAmount - stripeFee

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Cancel Booking</DialogTitle>
      <DialogContent>
        {isBuddyDive ? (
          <Box>
            <Typography>
              Are you sure you want to cancel your booking? No refund applies
              since no payment was made.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>Choose how you would like to be refunded:</Typography>

            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Typography variant='subtitle1' fontWeight='bold'>
                Credit Refund
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Full amount added to your account balance for future dives.
              </Typography>
              <Typography variant='h6'>
                {formatCurrency(transactionAmount)}
              </Typography>
              <Button
                variant='contained'
                color='primary'
                onClick={() => onConfirm('credit')}
                disabled={isProcessing}
                startIcon={
                  isProcessing ? <CircularProgress size={18} /> : undefined
                }
                sx={{ mt: 1 }}
                fullWidth
              >
                {isProcessing ? 'Processing...' : 'Credit Refund'}
              </Button>
            </Box>

            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Typography variant='subtitle1' fontWeight='bold'>
                Monetary Refund
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Returned to your original payment method. Stripe processing fees
                are deducted.
              </Typography>
              <Typography variant='h6'>
                {formatCurrency(monetaryRefundAmount)}
              </Typography>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => onConfirm('monetary')}
                disabled={isProcessing}
                startIcon={
                  isProcessing ? <CircularProgress size={18} /> : undefined
                }
                sx={{ mt: 1 }}
                fullWidth
              >
                {isProcessing ? 'Processing...' : 'Monetary Refund'}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <MuiLink
            component={RouterLink}
            to='/terms'
            variant='body2'
            underline='hover'
          >
            View our Terms &amp; Conditions
          </MuiLink>
        </Box>
      </DialogContent>

      <DialogActions>
        {isBuddyDive ? (
          <>
            <Button onClick={onClose} disabled={isProcessing}>
              Close
            </Button>
            <Button
              onClick={() => onConfirm('credit')}
              disabled={isProcessing}
              color='error'
              variant='contained'
              startIcon={
                isProcessing ? <CircularProgress size={18} /> : undefined
              }
            >
              {isProcessing ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} disabled={isProcessing}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CancellationDialog
